<script lang="ts" setup>
import { computed } from 'vue'
import { Color } from '@previo/codebooks'
import PDivider from '../divider/PDivider.vue'
import PIcon from '../icon/PIcon.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTextHighlighted from '../typography/body/PTextHighlighted.vue'
import PTextDescription from '../typography/system/PTextDescription.vue'
import { usePrevioDS } from '../../composables/helpers'
import { PStepperStatus } from './types'

interface Props {
  status: PStepperStatus
  icon: string
  text?: string
  description?: string
}

const props = withDefaults(defineProps<Props>(), {
  status: PStepperStatus.DEFAULT,
})

const ds = usePrevioDS()

const iconBg = computed<string>(() => {
  switch (props.status) {
    case PStepperStatus.DEFAULT:
      return `bg-${Color.GREY_50}`
    case PStepperStatus.ACTIVE:
      return `bg-${Color.POSITIVE}`
    case PStepperStatus.COMPLETED:
      return `bg-${Color.POSITIVE_TRANSPARENT}`

    default:
      throw 'Invalid prop of status in  PStepperStep'
  }
})

const iconColor = computed<Color.GREY_500 | Color.WHITE | Color.PRIMARY | Color.POSITIVE>(() => {
  switch (props.status) {
    case PStepperStatus.DEFAULT:
      return Color.GREY_500
    case PStepperStatus.ACTIVE:
      return Color.WHITE
    case PStepperStatus.COMPLETED:
      return Color.POSITIVE
    default:
      throw 'Invalid prop of status in  PStepperStep'
  }
})

const icon = computed(() => (props.status === PStepperStatus.COMPLETED ? 'check' : props.icon))

const descriptionColor = computed<Color.GREY_500 | null>(() => {
  return props.status !== PStepperStatus.ACTIVE ? Color.GREY_500 : null
})
</script>

<template>
  <div class="p-flex p-flex-1 p-items-center p-stepper-step-divider">
    <p-space :mobile="PSpaceSize.SM" />
    <p-divider class="p-flex-1" />
    <p-space :mobile="PSpaceSize.SM" />
  </div>
  <div class="p-flex p-flex-center">
    <p-icon class="ds-stepper-step-item" :class="iconBg" :color="iconColor" :icon="icon" />
    <p-space :mobile="PSpaceSize.MD" />
    <div
      v-if="!ds.screen().isMobile || props.status === PStepperStatus.ACTIVE"
      class="p-flex p-flex-direction-column"
    >
      <p-text-highlighted v-if="text" :text="text" />
      <p-text-description v-if="description" :text="description" :color="descriptionColor" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/ds.variables.module';
@import '../../assets/scss/shared/mixins';

.ds-stepper-step-item {
  height: 32px;
  width: 32px;
  border-radius: $global-border-radius;
  @include mobile {
    height: 40px;
    width: 40px;
  }
}
</style>
